/*
This file contains shared utilities between assignment types folder and components folder.
It prevents circular dependencies between those folders by housing functions that depend on modules from both folders.
*/
import { ELSLoggingService } from '@els/els-ui-common-react';
import { RecContentItemDto, RecContentItemTypeDto, SimulationLearningSystemRecContentTypeDto } from '../apis/rec-gateway/rec-gateway.dtos';
import { CatalogAdaptiveLessonExternalEntityDto,
  CatalogEvolveResourceExternalEntityDtoParsed,
  CatalogExternalEntityDto,
  CatalogExternalEntityDtoParsed,
  CatalogInteractiveReviewExternalEntityDtoParsed,
  CatalogLabSimulationImplementationModuleExternalEntityDto,
  CatalogOsmosisVideoExternalEntityDtoParsed,
  CatalogPatientReportExternalEntityDtoParsed,
  CatalogPreOrPostSimulationQuizExternalEntityDtoParsed,
  CatalogShadowHealthExternalEntityDtoParsed,
  CatalogSherpathCaseStudyExternalEntityDtoParsed,
  CatalogSherpathGroupActivityExternalEntityDtoParsed,
  CatalogSherpathLessonExternalEntityDtoParsed,
  CatalogSherpathPowerpointExternalEntityDtoParsed,
  CatalogSherpathSimulationExternalEntityDtoParsed,
  CatalogSherpathSkillExternalEntityDtoParsed,
  CatalogSimChartExternalEntityDtoParsed,
  CatalogVRSimulationImplementationModuleExternalEntityDto,
  CatalogWithExternalEntitiesDto,
  CommonSLSExternalEntityDtoParsed } from '../apis/sherpath-course-management-service/sherpath-course-management-service.dtos';
import { ExternalIdTypeDto } from '../apis/sherpath-syllabus-service/sherpath-syllabus-service.constants';
import { SyllabusItemDto } from '../apis/sherpath-syllabus-service/sherpath-syllabus-service.dtos';
import { getCustomExternalEntityHandler } from '../assignment-types/utils/external-entity.handlers';
import { fileName } from '../components/header-nav/navigation.utilities';
import { AssignmentDto, AssignmentType } from '../apis/eols-assessment-service/eols-assessment-service.dtos';
import { isStudent } from './common.utilities';
import { EbookPageRangeSeparators } from '../constants/common.constants';
import { getBookTaxonomy, getChapterPageRangesFromPageRanges, getEbookAssignmentTitle } from '../pages/ebook-assignment-editor/ebook-assignment.utilities';
import { PrimaryTaxonomy } from '../apis/rec-gateway/rec-gateway.models';

export const getCatalogItemFromSyllabusItem = (catalogDto: CatalogWithExternalEntitiesDto, syllabusItem: SyllabusItemDto): RecContentItemDto => {
  const externalId = syllabusItem.externalIdentifiers.find((item) => {
    return item.type === ExternalIdTypeDto.CATALOG_ITEM_ID;
  });

  if (externalId) {
    return catalogDto.catalog.data.find((item) => {
      return item.id === externalId.value;
    });
  }

  return null;
};

export const getExternalEntity = (contentItem: RecContentItemDto, catalog: CatalogWithExternalEntitiesDto): CatalogExternalEntityDtoParsed => {

  if (!contentItem || !catalog.externalEntities) {
    return null;
  }

  const externalEntity = catalog.externalEntities.find((_externalEntity) => {
    return _externalEntity.catalogItemId === contentItem.id;
  });

  if (!externalEntity || !externalEntity.entity) {
    return null;
  }

  const entityProps = {
    externalEntity,
    contentItem
  };

  const customExternalEntityHandler = getCustomExternalEntityHandler(contentItem.type);
  if (customExternalEntityHandler) {
    return customExternalEntityHandler(entityProps);
  }

  if ([
    RecContentItemTypeDto.EVOLVE_RESOURCE,
    RecContentItemTypeDto.SHADOW_HEALTH,
    RecContentItemTypeDto.SHERPATH_SIMULATION,
    RecContentItemTypeDto.SHERPATH_SKILL,
    RecContentItemTypeDto.SHERPATH_POWERPOINT,
    RecContentItemTypeDto.SHERPATH_GROUP_ACTIVITY,
    RecContentItemTypeDto.SHERPATH_CASE_STUDY,
    RecContentItemTypeDto.REVIEW_QUIZ,
    RecContentItemTypeDto.ACKNOWLEDGEMENTS,
    RecContentItemTypeDto.READING_ASSIGNMENT,
    RecContentItemTypeDto.VERBAL_REPORT_TEMPLATE,
    RecContentItemTypeDto.OBSERVER_EVALUATION_RUBRIC,
    RecContentItemTypeDto.COURSE_UPDATES,
    RecContentItemTypeDto.SCENARIO_INDEX,
    RecContentItemTypeDto.PERFORMANCE_CHECKLIST,
    RecContentItemTypeDto.IMPLEMENTATION_GUIDE,
    RecContentItemTypeDto.MOULAGE_MANUAL,
    RecContentItemTypeDto.PATIENT_REPORT,
    RecContentItemTypeDto.PRE_SIMULATION_QUIZ,
    RecContentItemTypeDto.POST_SIMULATION_QUIZ,
    RecContentItemTypeDto.SCMO_NG,
    RecContentItemTypeDto.VR_SIMULATION,
    RecContentItemTypeDto.PRE_SIMULATION_EXERCISE,
    RecContentItemTypeDto.POST_SIMULATION_EXERCISE,
    RecContentItemTypeDto.JOURNAL_EXERCISE,
    RecContentItemTypeDto.SBAR_EXERCISE,
    RecContentItemTypeDto.HESI_ASSESS
  ].includes(contentItem.type)) {

    let _parsedData;

    const _externalEntity = externalEntity as
       CatalogEvolveResourceExternalEntityDtoParsed
      | CatalogShadowHealthExternalEntityDtoParsed
      | CatalogSherpathSimulationExternalEntityDtoParsed
      | CatalogSherpathSkillExternalEntityDtoParsed
      | CatalogSherpathCaseStudyExternalEntityDtoParsed
      | CatalogSherpathPowerpointExternalEntityDtoParsed
      | CatalogSherpathGroupActivityExternalEntityDtoParsed
      | CatalogInteractiveReviewExternalEntityDtoParsed
      | CatalogPreOrPostSimulationQuizExternalEntityDtoParsed
      | CatalogPatientReportExternalEntityDtoParsed
      | CommonSLSExternalEntityDtoParsed;

    try {
      _parsedData = JSON.parse(_externalEntity.entity.data);
    } catch (e) {
      _parsedData = {
        // Amazingly this fallback works because all these external entity types share a title property
        title: `${contentItem.type} - ${contentItem.id}`
      };
      ELSLoggingService.error(
        fileName,
        `Unable to parse external entity ${contentItem.id}`,
      );
    }

    return {
      ..._externalEntity,
      _parsedData
    };
  }

  if ([
    RecContentItemTypeDto.LAB_SIMULATION_IMPLEMENTATION_MODULE,
    RecContentItemTypeDto.VR_SIMULATION_IMPLEMENTATION_MODULE,
  ].includes(contentItem.type)) {
    return externalEntity as CatalogLabSimulationImplementationModuleExternalEntityDto
      | CatalogVRSimulationImplementationModuleExternalEntityDto;
  }

  return null;

};

export const getExternalEntityClone = (contentItem: RecContentItemDto, externalEntitiesMap: Map<string, CatalogExternalEntityDto>): CatalogExternalEntityDtoParsed => {

  if (!contentItem || !externalEntitiesMap.size) {
    return null;
  }

  const externalEntity = externalEntitiesMap.get(contentItem.id);

  if (!externalEntity || !externalEntity.entity) {
    return null;
  }

  const entityProps = {
    externalEntity,
    contentItem
  };

  const customExternalEntityHandler = getCustomExternalEntityHandler(contentItem.type);
  if (customExternalEntityHandler) {
    return customExternalEntityHandler(entityProps);
  }

  if ([
    RecContentItemTypeDto.EVOLVE_RESOURCE,
    RecContentItemTypeDto.SHADOW_HEALTH,
    RecContentItemTypeDto.SHERPATH_SIMULATION,
    RecContentItemTypeDto.SHERPATH_SKILL,
    RecContentItemTypeDto.SHERPATH_POWERPOINT,
    RecContentItemTypeDto.SHERPATH_GROUP_ACTIVITY,
    RecContentItemTypeDto.SHERPATH_CASE_STUDY,
    RecContentItemTypeDto.REVIEW_QUIZ,
    RecContentItemTypeDto.ACKNOWLEDGEMENTS,
    RecContentItemTypeDto.READING_ASSIGNMENT,
    RecContentItemTypeDto.VERBAL_REPORT_TEMPLATE,
    RecContentItemTypeDto.OBSERVER_EVALUATION_RUBRIC,
    RecContentItemTypeDto.COURSE_UPDATES,
    RecContentItemTypeDto.SCENARIO_INDEX,
    RecContentItemTypeDto.IMPLEMENTATION_GUIDE,
    RecContentItemTypeDto.MOULAGE_MANUAL,
    RecContentItemTypeDto.PATIENT_REPORT,
    RecContentItemTypeDto.PRE_SIMULATION_QUIZ,
    RecContentItemTypeDto.POST_SIMULATION_QUIZ,
    RecContentItemTypeDto.SCMO_NG,
    RecContentItemTypeDto.VR_SIMULATION,
    RecContentItemTypeDto.PRE_SIMULATION_EXERCISE,
    RecContentItemTypeDto.POST_SIMULATION_EXERCISE,
    RecContentItemTypeDto.JOURNAL_EXERCISE,
    RecContentItemTypeDto.SBAR_EXERCISE,
    RecContentItemTypeDto.PERFORMANCE_CHECKLIST,
    RecContentItemTypeDto.HESI_ASSESS
  ].includes(contentItem.type)) {

    let _parsedData;

    const _externalEntity = externalEntity as
        CatalogEvolveResourceExternalEntityDtoParsed
      | CatalogShadowHealthExternalEntityDtoParsed
      | CatalogSherpathSimulationExternalEntityDtoParsed
      | CatalogSherpathSkillExternalEntityDtoParsed
      | CatalogSherpathCaseStudyExternalEntityDtoParsed
      | CatalogSherpathPowerpointExternalEntityDtoParsed
      | CatalogSherpathGroupActivityExternalEntityDtoParsed
      | CatalogInteractiveReviewExternalEntityDtoParsed
      | CatalogPreOrPostSimulationQuizExternalEntityDtoParsed
      | CatalogPatientReportExternalEntityDtoParsed
      | CommonSLSExternalEntityDtoParsed;

    try {
      _parsedData = JSON.parse(_externalEntity.entity.data);
    } catch (e) {
      _parsedData = {
        // Amazingly this fallback works because all these external entity types share a title property
        title: `${contentItem.type} - ${contentItem.id}`
      };
      ELSLoggingService.error(
        fileName,
        `Unable to parse external entity ${contentItem.id}`,
      );
    }

    return {
      ..._externalEntity,
      _parsedData
    };
  }

  if ([
    RecContentItemTypeDto.LAB_SIMULATION_IMPLEMENTATION_MODULE,
    RecContentItemTypeDto.VR_SIMULATION_IMPLEMENTATION_MODULE
  ].includes(contentItem.type)) {
    return externalEntity as CatalogLabSimulationImplementationModuleExternalEntityDto
      | CatalogVRSimulationImplementationModuleExternalEntityDto;
  }

  return null;

};

export const getEnhancedLesson = (contentItem: RecContentItemDto, catalog: CatalogWithExternalEntitiesDto): CatalogAdaptiveLessonExternalEntityDto => {
  if (!contentItem || contentItem.type !== RecContentItemTypeDto.ADAPTIVE_LESSON) {
    return null;
  }
  return getExternalEntity(contentItem, catalog) as CatalogAdaptiveLessonExternalEntityDto;
};

export const getEnhancedLessonClone = (contentItem: RecContentItemDto, externalEntitiesMap: Map<string, CatalogExternalEntityDto>): CatalogAdaptiveLessonExternalEntityDto => {
  if (!contentItem || contentItem.type !== RecContentItemTypeDto.ADAPTIVE_LESSON) {
    return null;
  }
  return getExternalEntityClone(contentItem, externalEntitiesMap) as CatalogAdaptiveLessonExternalEntityDto;
};

export const getSimulationLearningSystemResource = <T extends unknown>(
  contentItem: RecContentItemDto,
  catalog: CatalogWithExternalEntitiesDto
): T | null => {
  if (!contentItem || !SimulationLearningSystemRecContentTypeDto.includes(contentItem.type)) {
    return null;
  }
  return getExternalEntity(contentItem, catalog) as T | null;
};

export const getSimulationLearningSystemResourceClone = <T extends unknown>(
  contentItem: RecContentItemDto,
  externalEntitiesMap: Map<string, CatalogExternalEntityDto>
): T | null => {
  if (!contentItem || !SimulationLearningSystemRecContentTypeDto.includes(contentItem.type)) {
    return null;
  }
  return getExternalEntityClone(contentItem, externalEntitiesMap) as T | null;
};

export const getSimchart = (contentItem: RecContentItemDto, catalog: CatalogWithExternalEntitiesDto): CatalogSimChartExternalEntityDtoParsed => {

  if (!contentItem || contentItem.type !== RecContentItemTypeDto.SIM_CHART) {
    return null;
  }
  return getExternalEntity(contentItem, catalog) as CatalogSimChartExternalEntityDtoParsed;
};

export const getSimchartClone = (contentItem: RecContentItemDto, externalEntitiesMap: Map<string, CatalogExternalEntityDto>): CatalogSimChartExternalEntityDtoParsed => {

  if (!contentItem || contentItem.type !== RecContentItemTypeDto.SIM_CHART) {
    return null;
  }
  return getExternalEntityClone(contentItem, externalEntitiesMap) as CatalogSimChartExternalEntityDtoParsed;
};

export const getShadowHealth = (contentItem: RecContentItemDto, catalog: CatalogWithExternalEntitiesDto): CatalogShadowHealthExternalEntityDtoParsed => {
  if (!contentItem || contentItem.type !== RecContentItemTypeDto.SHADOW_HEALTH) {
    return null;
  }
  return getExternalEntity(contentItem, catalog) as CatalogShadowHealthExternalEntityDtoParsed;
};

export const getShadowHealthClone = (contentItem: RecContentItemDto, externalEntitiesMap: Map<string, CatalogExternalEntityDto>): CatalogShadowHealthExternalEntityDtoParsed => {
  if (!contentItem || contentItem.type !== RecContentItemTypeDto.SHADOW_HEALTH) {
    return null;
  }
  return getExternalEntityClone(contentItem, externalEntitiesMap) as CatalogShadowHealthExternalEntityDtoParsed;
};

export const isOpenAuthessInNewTab = (props: {
  isNewTabEnabled: boolean;
  assignment: AssignmentDto;
  userRole: string;
  isAuthessEnabled: boolean;
}): boolean => {
  const {
    isNewTabEnabled,
    assignment,
    userRole,
    isAuthessEnabled
  } = props;
  return Boolean(isNewTabEnabled
    && !isAuthessEnabled
    && isStudent(userRole)
    && assignment
    && assignment.assignmentType === AssignmentType.AUTHESS);
};

export const getSherpathLesson = (contentItem: RecContentItemDto, catalog: CatalogWithExternalEntitiesDto): CatalogSherpathLessonExternalEntityDtoParsed => {
  if (!contentItem || contentItem.type !== RecContentItemTypeDto.SHERPATH_LESSON) {
    return null;
  }
  return getExternalEntity(contentItem, catalog) as CatalogSherpathLessonExternalEntityDtoParsed;
};

// eslint-disable-next-line max-len
export const getSherpathLessonClone = (contentItem: RecContentItemDto, externalEntitiesMap: Map<string, CatalogExternalEntityDto>): CatalogSherpathLessonExternalEntityDtoParsed => {
  if (!contentItem || contentItem.type !== RecContentItemTypeDto.SHERPATH_LESSON) {
    return null;
  }
  return getExternalEntityClone(contentItem, externalEntitiesMap) as CatalogSherpathLessonExternalEntityDtoParsed;
};

export const getHesiAssessExternalEntity = (contentItem: RecContentItemDto, catalog: CatalogWithExternalEntitiesDto): CatalogExternalEntityDtoParsed => {
  if (!contentItem || contentItem.type !== RecContentItemTypeDto.HESI_ASSESS) {
    return null;
  }
  return getExternalEntity(contentItem, catalog);
};

export const getHesiAssessExternalEntityClone = (contentItem: RecContentItemDto, externalEntitiesMap: Map<string, CatalogExternalEntityDto>): CatalogExternalEntityDtoParsed => {
  if (!contentItem || contentItem.type !== RecContentItemTypeDto.HESI_ASSESS) {
    return null;
  }
  return getExternalEntityClone(contentItem, externalEntitiesMap);
};

export const getOsmosisVideo = (contentItem: RecContentItemDto, catalog: CatalogWithExternalEntitiesDto): CatalogOsmosisVideoExternalEntityDtoParsed => {
  if (!contentItem || contentItem.type !== RecContentItemTypeDto.OSMOSIS_VIDEO) {
    return null;
  }
  return getExternalEntity(contentItem, catalog) as CatalogOsmosisVideoExternalEntityDtoParsed;
};

export const getOsmosisVideoClone = (contentItem: RecContentItemDto, externalEntitiesMap: Map<string, CatalogExternalEntityDto>): CatalogOsmosisVideoExternalEntityDtoParsed => {
  if (!contentItem || contentItem.type !== RecContentItemTypeDto.OSMOSIS_VIDEO) {
    return null;
  }
  return getExternalEntityClone(contentItem, externalEntitiesMap) as CatalogOsmosisVideoExternalEntityDtoParsed;
};
export const getEbookReadingPartsFromCatalogId = (id: string) => {
  return id.split('ebook-reading:')[1].split('/');
};

export const getIsbnFromEbookCatalogItemId = (id: string) => {
  return getEbookReadingPartsFromCatalogId(id)[0];
};

export const getIsbnFromEbookSyllabusItem = (syllabusItem: SyllabusItemDto): string => {
  const eid = syllabusItem.externalIdentifiers.find(x => x.type === ExternalIdTypeDto.CATALOG_ITEM_ID);
  if (!eid) {
    return null;
  }
  return getIsbnFromEbookCatalogItemId(eid.value);
};

export const getPageRangesFromEbookCatalogItemId = (id: string): string => {
  const value = getEbookReadingPartsFromCatalogId(id)[1];
  if (!value) {
    return value;
  }
  return value.replace(/;/g, EbookPageRangeSeparators.CHUNK_SEPARATOR);
};

export const getPageRangesFromEbookSyllabusItem = (syllabusItem: SyllabusItemDto): string => {
  const eid = syllabusItem.externalIdentifiers.find(x => x.type === ExternalIdTypeDto.CATALOG_ITEM_ID);
  if (!eid) {
    return null;
  }
  return getPageRangesFromEbookCatalogItemId(eid.value);
};

export const getEbookReadingTitle = (contentItem: RecContentItemDto, primaryTaxonomies: PrimaryTaxonomy[]): string => {
  const pageRanges = getPageRangesFromEbookCatalogItemId(contentItem.id);
  const isbn = getIsbnFromEbookCatalogItemId(contentItem.id);
  const bookTaxonomy = getBookTaxonomy(isbn, primaryTaxonomies);
  if (!bookTaxonomy) {
    return null;
  }
  const chapterPageRangeMap = getChapterPageRangesFromPageRanges(pageRanges, bookTaxonomy);

  return getEbookAssignmentTitle(chapterPageRangeMap, bookTaxonomy, false);
};
